import React from "react";
import { useHistory } from "react-router";

import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import uuidv4 from "uuid/v4";

import { AppContext } from "~/common";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isiOSApp, isTeams } from "~/lib/utils";
import { Button } from "~/ui";
import { HelpCenterLink, Modal } from "~/ui/legacyFellowIndex";

import { FeatureInfo, type FeatureValues } from "./utils";

import styles from "./css/PaidFeatureModal.module.css";

export default function PaidFeatureModal({
	id,
	close,
	modalContentOnly,
}: {
	id: FeatureValues;
	close?: () => void;
	modalContentOnly?: boolean;
}) {
	const history = useHistory();
	const { user } = React.useContext(AppContext);
	const uxSimplificationEnabled = user?.uxSimplificationEnabled;

	const { title, boldText, description, product, videoURL, withBorder, image } = FeatureInfo[id];
	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
			ux: "modal",
			feature: id,
		}),
		[id],
	);

	React.useEffect(() => {
		dispatchAnalyticsTrack("Paywall Encountered", analyticsProperties);
	}, [analyticsProperties]);

	const closeModal = React.useCallback(() => {
		close?.();
		dispatchAnalyticsTrack("Paywall Dismissed", analyticsProperties);
	}, [close, analyticsProperties]);

	const onUpgradeClick = React.useCallback(() => {
		if (isTeams) {
			close?.();
			window.open(`${window.location.origin}/account/billing/plan/?from=upgrade`, "_blank", "noopener");
			return;
		}
		history.push("/account/billing/plan/?from=upgrade");
		close?.();
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_billing" });
	}, [close, history, analyticsProperties]);

	const onLearnClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_marketing" });
	}, [analyticsProperties]);

	const featureTriggeringTheUpgrade = (
		<span className={cx(styles.title, { [styles[product.family]]: true })}>
			{uxSimplificationEnabled ? title : <strong>{title}</strong>}
		</span>
	);

	const modalContent = (
		<div className={cx({ [styles.simplifiedUx]: uxSimplificationEnabled })}>
			<Modal.Header onClose={closeModal} className={styles.header}>
				<Modal.Title className={styles.titleWrapper}>
					{isiOSApp ? (
						<>{featureTriggeringTheUpgrade} is currently not available for your account</>
					) : (
						<>Upgrade to unlock {featureTriggeringTheUpgrade}</>
					)}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<p>
					<strong>{boldText}</strong> {description}{" "}
					<HelpCenterLink articleId={product.helpArticleId} onClick={onLearnClick}>
						Learn more
					</HelpCenterLink>
				</p>
				{videoURL && (
					<video
						autoPlay
						loop
						muted
						src={videoURL}
						preload="auto"
						width="100%"
						crossOrigin="anonymous"
						className={cx({ [styles.videoBorder]: withBorder })}
					/>
				)}
				{image}
			</Modal.Body>
			{!isiOSApp && (
				<Modal.Footer className={styles.footer}>
					<Button size="normal" variant="billing" onClick={onUpgradeClick} className={styles.upgradeButton}>
						<FontAwesomeIcon icon={faArrowAltCircleUp} />
						&nbsp; Upgrade
					</Button>
				</Modal.Footer>
			)}
		</div>
	);

	if (modalContentOnly) {
		return modalContent;
	} else {
		return (
			<Modal onClose={closeModal} width="45.25rem">
				{modalContent}
			</Modal>
		);
	}
}
